
import { Vue, Component, Prop } from 'vue-property-decorator';
import Processing from '@/components/common/Processing.vue';

@Component({
	components: {
		Processing,
	},
})
export default class UiButton extends Vue {
	@Prop({ type: String, default: undefined }) readonly buttonText?: string;
	@Prop({ type: String, default: undefined }) readonly variant!:
		| string
		| undefined;
	@Prop({ type: String, default: undefined }) readonly size!:
		| string
		| undefined;
	@Prop({ type: String, default: undefined }) readonly color!:
		| string
		| undefined;
	@Prop({ type: String, default: undefined }) readonly textClass!:
		| string
		| undefined;
	// icon prop is deprecated and will be removed with JR-156
	@Prop({ type: String, default: undefined }) readonly icon!:
		| string
		| undefined;
	@Prop({ type: Boolean, default: false }) readonly fullWidth!: boolean;
	@Prop({ type: Boolean, default: false }) readonly processing!: boolean;
	@Prop({ type: String, default: 'button' }) readonly type!: string;
	@Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
	@Prop({ type: Boolean, default: false }) readonly rounded!: boolean;
	@Prop({ type: Boolean, default: false }) readonly contentLeft!: boolean;
	@Prop({ type: Boolean, default: false })
	readonly disableIconPresetStyle!: boolean;
	@Prop({ type: String, default: 'bg-secondary-100' })
	readonly buttonClasses!: string;
	@Prop({ type: String, default: undefined }) readonly id!: string;

	isHovered: boolean = false;

	get isIconSlotUsed(): boolean {
		return Boolean(this.$slots.icon);
	}

	get isDisabled(): boolean {
		return this.disabled || this.processing;
	}

	get buttonClass(): { [key: string]: boolean } {
		const transparent = ['linkButton', 'secondary'].some(
			(variant) => variant === this.variant
		);

		return {
			[this.borderStyles]: true,
			[this.variantStyle]: true,
			[this.variantSize]: true,
			[this.contentPosition]: true,
			['w-full flex']: this.fullWidth,
			['disabled:bg-gray-300']: this.disabled && !transparent,
			[this.buttonClasses]: true,
		};
	}

	get cursor(): string {
		return this.isDisabled ? 'cursor-default' : 'cursor-pointer';
	}

	get contentPosition(): string {
		return this.contentLeft ? '' : 'justify-center';
	}

	get variantStyle(): string {
		switch (this.variant) {
			case 'green':
				return 'border-transparent text-blue-900 font-extrabold bg-emphasizegreen hover:bg-emphasizegreen focus:outline-none focus:border-blue-500 focus:shadow-outline-blue active:bg-blue-500';
			case 'warning':
				return 'focus:outline-none border-primary-500 text-primary hover:border-primary-800 hover:text-primary-800 text-xs bg-white';
			case 'secondary': {
				return `border-secondary text-secondary hover:border-secondary-700 hover:text-secondary-700 focus:outline-none focus:text-secondary-700 active:border-secondary-700 active:text-secondary-700 font-semibold disabled:border-gray-300 disabled:text-gray-300 ${this.cursor}`;
			}
			case 'empty':
				return `text-gray-700 hover:text-gray-500 active:text-gray-800 active:bg-gray-50 focus:outline-none ${this.cursor}`;
			case 'outline':
				return `border-gray-300 text-gray-700 bg-white emphasizyellow hover:text-gray-500 focus:outline-none customOutline active:text-gray-800 active:bg-gray-50 disabled:border-transparent ${this.cursor}`;
			case 'outline-black':
				return `rounded-full border border-solid border-gray-800 bg-white text-primary-black text-xs hover:text-primary-black focus:outline-none customOutline active:primary-black active:bg-white disabled:border-none ${this.cursor}`;
			case 'unbordered':
				return `text-gray-700 bg-white focus:outline-none active:text-gray-800 active:bg-gray-50 hover:bg-gray-100 hover:text-gray-900 rounded-none ${this.cursor}`;
			case 'danger':
				return `danger-buttons disabled:text-white disabled:border-none ${this.cursor}`;
			case 'linkButton':
				return `text-primary-500 hover:text-primary-800 hover:no-underline underline focus:outline-none ${this.cursor}`;
			case 'link':
				return `cursor-pointer text-badgeBlue hover:text-primary-500 focus:outline-none focus:underline transition ease-in-out duration-150`;
			case 'emphasize':
				return `border-transparent text-white bg-emphasize focus:outline-none  focus:shadow-outline-primary font-semibold ${this.cursor}`;
			case 'accent':
				return 'border-transparent text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:border-purple-700 focus:shadow-outline-purple active:bg-purple-700';
			case 'filter':
				return 'border-transparent text-black font-black-200 bg-emphasizegray hover:bg-emphasizegray focus:outline-none focus:border-gray-500 focus:shadow-outline-gray active:bg-gray-500';
			case 'blue':
				return 'border-transparent text-white bg-emphasizeblue disabled:bg-gray-300 disabled:text-gray-800 hover:bg-emphasizeblue focus:outline-none focus:border-blue-500 focus:shadow-outline-blue active:bg-blue-500';
			case 'blueBlack':
				return 'text-black bg-white disabled:bg-gray-300 disabled:text-gray-800 border-emphasizeblue hover:bg-emphasizeblue focus:outline-none focus:border-blue-500 focus:shadow-outline-blue active:bg-blue-500 borderBlue';
			case 'black':
				return 'border-transparent text-white text-sm font-normal emphasizyellow bg-button-black active:bg-button-black active:text-white focus:outline-none focus:shadow-outline-none';
			case 'gray':
				return 'border-storm-gray text-secondary-black text-base emphasizyellow disabled:text-primary-black font-normal bg-white  focus:outline-none focus:border-button-black focus:shadow-outline-button-black active:button-black';
			case 'transparent':
				return 'bg-transparent text-sm customLineH font-normal text-blue focus:outline-none border-none buttonHeight';
			case 'white':
				return 'customWhiteVariant focus:outline-none focus:shadow-outline-none emphasizyellow';
			case 'default':
			default:
				return `border-transparent text-white text-sm font-normal emphasizyellow bg-button-black active:bg-button-black active:text-white focus:outline-none focus:shadow-outline-none`;
		}
	}

	get iconSizeStyle(): string {
		switch (this.size) {
			case 'xl':
				return 'h-6 w-6 -m-3 -ml-1 mr-2';
			case 'no':
				return 'h-5 w-5';
			case 'project':
				return 'h-1 w-5';
			default:
				return 'h-4 w-4 mr-2';
		}
	}

	get iconStyle(): string {
		return this.disableIconPresetStyle ? '' : this.iconSizeStyle;
	}

	get borderStyles(): string {
		return 'rounded-3xl';
	}

	get variantSize(): string {
		switch (this.size) {
			case 'exs':
				return 'px-1.5 text-xs leading-4 ';
			case 'project':
				return 'px-10 py-3 text-sm space-x-2';
			case 'no':
				return '';
			case 'xxs':
				return 'px-2.5 py-1.5 text-sm leading-3';
			case 'xs':
				return 'px-2.5 py-1.5 text-sm leading-4 ';
			case 'sm':
				return 'px-3.5 py-2 text-sm leading-4';
			case 'low':
				return 'text-low';
			case 'calendar':
				return 'px-2 py-1 text-xs leading-normal h-6 buttonWidth';
				case 'md':
				return 'px-2 py-1 text-sm leading-normal h-8';
			case 'lg':
				return 'px-4 py-2 text-sm leading-normal h-8';
			case 'lg2':
				return 'pl-4 pr-6 py-2 text-base leading-6';
			case 'xl':
				return 'px-6 py-3 text-lg leading-6';
			case '2xl':
				return 'p-4 sm:px-7 sm:py-3 text-base leading-6 font-normal';
			case '3xl':
				return 'px-8 py-3 text-2xl leading-6';

			default:
				if (this.variant === 'linkButton') {
					return 'px-2.5 py-2';
				}

				if (this.variant === 'warning') {
					return 'h-6.5 px-2';
				}

				return 'px-7 py-3 text-sm h-10';
		}
	}

	get buttonTextStyle(): string | undefined {
		if (!this.textClass && this.variant == 'linkButton') {
			return 'buttonTextDefault';
		}
		if (this.isDisabled) return 'text-storm-gray';
		return this.textClass;
	}

	get processingOpacity(): number | undefined {
		if (this.variant === 'linkButton') {
			return 25;
		}
		return undefined;
	}
}
